import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  BaseWebsocketMessage,
  GetWebsocketMessageRequest,
  SubWebsocketMessageRequest,
  WebsocketEntTypes,
  WebSocketService,
  WSEntityManager,
} from '@qtek/core/websockets-core';
import {
  BackendResponse,
  Customer,
  Person,
  PUBLIC_ACCESS,
  TokenAction,
} from '@qtek/shared/models';
import { getGMTOffset } from '@qtek/shared/utils';
import { Observable } from 'rxjs';
import { HttpEntityManager } from './../http-entity-manager';

@Injectable({
  providedIn: 'root',
})
export class CustomersService extends HttpEntityManager<Customer, Customer> {
  entityName: WebsocketEntTypes = 'relcnt';
  wsManager = new WSEntityManager(this.websocketService, this.entityName);

  constructor(
    protected http: HttpClient,
    private websocketService: WebSocketService
  ) {
    super(true);
  }

  subscribe(
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>
  ): Observable<BaseWebsocketMessage> {
    return this.wsManager.subscribeEntity(payload, true);
  }

  createCustomer(payload: Customer) {
    return super.post(payload, undefined, {
      gmtOffset: getGMTOffset(),
      tzGuess: btoa(Intl.DateTimeFormat().resolvedOptions().timeZone),
    });
  }

  deleteCustomerInfo(relId: string) {
    return super.delete(btoa(relId), {
      tp: 'val',
    });
  }

  deleteCustomer(relId: string) {
    return super.delete(btoa(relId), {
      tp: 'force',
    });
  }

  updateCustomer(payload: Customer | Person, relId: string) {
    return super.put(payload, btoa(relId));
  }

  updateInstitution(payload: Customer) {
    return super.put(payload);
  }

  sendInvitation(relId: string, query: any) {
    const key = '_snd';
    const keyLanguage = 'lng';
    const value = Object.keys(query.sendingMethod)
      .filter(k => query.sendingMethod[k])
      .join('.');

    let params = new HttpParams().set(key, value);

    if (query.language) {
      params = params.append(keyLanguage, query.language);
    }

    return this.http.post<BackendResponse>(
      `/api/v1/service/snd/relcnt/${btoa(relId)}`,
      undefined,
      {
        ...this.baseRequestOptions,
        params,
      }
    );
  }

  getTokenToUploadLogo(relId: string): Observable<{ res: any }> {
    return this.http.post<{ res: any }>(
      '/api/v1/service/doc/tkn',
      {
        refId: `acn=${relId}`,
        attr: 1000,
        action: TokenAction.UPLOAD,
        access: PUBLIC_ACCESS,
      },
      {
        ...this.baseRequestOptions,
      }
    );
  }

  sendLogoImg(formData: FormData): Observable<any> {
    return this.http.post<BackendResponse>(`/api/v1/service/doc`, formData, {
      ...this.baseRequestOptions,
    });
  }

  subscribeSingleContactById(
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>
  ): Observable<BaseWebsocketMessage> {
    return this.wsManager.subscribeEntity(payload, false);
  }

  getSingleContactById(
    payload: Omit<GetWebsocketMessageRequest, 'ent' | 'op'>
  ) {
    this.wsManager.get(payload);
  }
}
